/**
 * ⌘K command palette, hosted in OrganizationLayout so it's available on every
 * /admin route. Four sources:
 *   1. Actions — the registry in ./paletteActions.tsx: create flows (via
 *      deep-link params the target pages read in effects), copy site link,
 *      tours (300ms-deferred), help assistant, settings shortcuts.
 *   2. Navigation — the shared registry in Nav/navItems.tsx (same data the
 *      sidebar renders, so the two can never drift).
 *   3. Organization switcher — the admin's other orgs (mirrors Nav's
 *      onOrganizationChange: setActive + hard reload to /admin/dashboard).
 *   4. Quick search — debounced, org-scoped GET /teams/search + /players/search
 *      (both regex-escaped + capped server-side).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import _ from 'lodash';
import { Building2, User, Users } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useAuth, useUser, useOrganizationList } from '@/auth/clerkShim';
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';
import { getFlatNavItems, OPEN_COMMAND_PALETTE_EVENT, type NavContext } from '@/components/Nav/navItems';
import { getPaletteActions, type PaletteActionContext } from './paletteActions';
import useTourStore from '@/store/tourStore';
import useAdminStore from '@/store/adminStore';

const baseUrl = `${process.env.API_URL}/api/v1`;

/** Search hits are rendered by id/name only — this is the slice actually read. */
interface TeamHit { _id: string; name?: string; [key: string]: unknown }
interface PlayerHit { _id: string; name?: string; team?: { _id: string } | null }
interface Membership { organization: { id: string; name: string } }
/** Nav target: either a registry item or an ad-hoc `{ href }`. */
interface GoTarget { href: string; newTab?: boolean }

export default function CommandPalette({ league, leagueName }: NavContext) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [teams, setTeams] = useState<TeamHit[]>([]);
  const [players, setPlayers] = useState<PlayerHit[]>([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();
  const { user } = useUser();
  const { setActive } = useOrganizationList();
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const requestSeq = useRef(0);

  const navItems = useMemo(
    () => getFlatNavItems({ league, leagueName }),
    [league, leagueName]
  );

  const actions = useMemo(() => getPaletteActions(), []);
  const markTourCompleted = useTourStore((s) => s.markCompleted);
  const clerkId = useAdminStore((s: any) => s.admin)?.provider?.id;
  const { getToken } = auth;
  const actionCtx: PaletteActionContext = useMemo(
    () => ({
      navigate,
      leagueName: leagueName || '',
      clerkId,
      getToken,
      markTourCompleted,
    }),
    [navigate, leagueName, clerkId, getToken, markTourCompleted]
  );

  // ⌘K / Ctrl+K toggle + the sidebar Search button's custom event.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, onOpenEvent);
    };
  }, []);

  useEffect(() => {
    if (open && user?.getOrganizationMemberships) {
      user.getOrganizationMemberships().then(setMemberships).catch(() => {});
    }
  }, [open, user]);

  // Reset transient state when the dialog closes.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setTeams([]);
      setPlayers([]);
    }
  }, [open]);

  const runSearch = useMemo(
    () =>
      _.debounce(async (q: string, orgName: string, seqRef: { current: number }) => {
        const seq = ++seqRef.current;
        setSearching(true);
        try {
          const [teamRes, playerRes] = await Promise.all([
            axios.get(`${baseUrl}/teams/search/?q=${encodeURIComponent(q)}&leagueName=${encodeURIComponent(orgName)}`),
            axios.get(`${baseUrl}/players/search/?q=${encodeURIComponent(q)}&leagueName=${encodeURIComponent(orgName)}&include=team`),
          ]);
          if (seq !== seqRef.current) return; // a newer query superseded this one
          setTeams((teamRes.data?.result || []).slice(0, 5));
          setPlayers((playerRes.data?.result || []).slice(0, 5));
        } catch (err) {
          if (seq === seqRef.current) {
            setTeams([]);
            setPlayers([]);
          }
        } finally {
          if (seq === seqRef.current) setSearching(false);
        }
      }, 250),
    []
  );

  useEffect(() => {
    if (query.trim().length >= 2 && leagueName) {
      runSearch(query.trim(), leagueName, requestSeq);
    } else {
      runSearch.cancel();
      setTeams([]);
      setPlayers([]);
      setSearching(false);
    }
  }, [query, leagueName, runSearch]);

  const go = useCallback(
    (item: GoTarget) => {
      setOpen(false);
      if (item.newTab) {
        window.open(item.href, '_blank', 'noopener');
      } else {
        navigate(item.href);
      }
    },
    [navigate]
  );

  const switchOrg = (organizationId: string) => {
    setOpen(false);
    setActive({ organization: organizationId });
    setTimeout(() => {
      window.location.href = '/admin/dashboard';
    }, 10);
  };

  // We request `?include=team`, so player.team is the populated DivisionTeam —
  // the team detail page is the closest destination we can link a player to.
  const playerTeamId = (player: PlayerHit) =>
    player.team && player.team._id ? player.team._id : null;

  const otherOrgs = memberships.filter((m) => m.organization.id !== auth.orgId);

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Command palette"
      description="Jump to a page, switch organizations, or find a team or player"
    >
      <CommandInput
        placeholder="Search pages, teams, players…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>{searching ? 'Searching…' : 'No results found.'}</CommandEmpty>
        <CommandGroup heading="Actions">
          {actions.map((action) => (
            <CommandItem
              key={`action-${action.id}`}
              value={`${action.name} ${action.keywords}`}
              onSelect={() => {
                setOpen(false);
                action.run(actionCtx);
              }}
              className="cursor-pointer"
            >
              <action.Icon width={16} height={16} />
              <span>{action.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Navigate">
          {navItems.map((item) => (
            <CommandItem
              key={`nav-${item.name}`}
              value={`${item.name} ${item.section}`}
              onSelect={() => go(item)}
              className="cursor-pointer"
            >
              <item.Icon width={16} height={16} />
              <span>{item.name}</span>
              <span className="ml-auto text-xs text-muted-foreground">{item.section}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        {otherOrgs.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Switch organization">
              {otherOrgs.map((m) => (
                <CommandItem
                  key={`org-${m.organization.id}`}
                  value={`switch ${m.organization.name}`}
                  onSelect={() => switchOrg(m.organization.id)}
                  className="cursor-pointer"
                >
                  <Building2 width={16} height={16} />
                  <span>{m.organization.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {teams.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Teams">
              {teams.map((team) => (
                <CommandItem
                  key={`team-${team._id}`}
                  value={`team ${getTeamDisplayName(team)} ${team._id}`}
                  onSelect={() => go({ href: `/admin/team/${team._id}` })}
                  className="cursor-pointer"
                >
                  <Users width={16} height={16} />
                  <span>{getTeamDisplayName(team)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {players.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Players">
              {players.map((player) => {
                const teamId = playerTeamId(player);
                return (
                  <CommandItem
                    key={`player-${player._id}`}
                    value={`player ${player.name} ${player._id}`}
                    disabled={!teamId}
                    onSelect={() => teamId && go({ href: `/admin/team/${teamId}` })}
                    className="cursor-pointer"
                  >
                    <User width={16} height={16} />
                    <span>{player.name}</span>
                    {!teamId && (
                      <span className="ml-auto text-xs text-muted-foreground">no team</span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
